import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import type { BomLibraryStatus, LibraryBomRow } from './types'
import { problemTitle } from './usePartsLibrary'

/**
 * The data-layer seam for the Parts Library's BOMs tab (UID-290). `GET
 * /api/parts` was supposed to carry `listing.boms`, but the pg library
 * repository hard-codes that field to `[]` (a backend gap tracked
 * separately) — so the tab and its tab-badge count read from the dedicated
 * `GET /boms` endpoint instead, which already returns the tenant's real
 * BOMs. This is a SEPARATE fetch from `usePartsLibrary`'s parts-tab
 * listing, with its own loading/error state, so a slow/broken BOMs call
 * never blocks or fails the parts tab (and vice versa).
 */
export interface BomsService {
  listBoms(): Promise<readonly LibraryBomRow[]>
  /** UID-295 (AC4) — change a BOM's status directly from the BOMs-tab card grid
   * (`PATCH /boms/:bomId`, status). Mirrors `useBomDetail`'s status update but on
   * the LIST data layer. Resolves void on success (the hook patches the row's
   * status in place). OPTIONAL so existing `{ listBoms }` doubles still satisfy the
   * interface; `defaultBomsService` always provides it. */
  updateStatus?(bomId: string, status: BomLibraryStatus): Promise<void>
}

/**
 * REAL BFF-backed BOMs list. The contract's `LibraryBomRow` (`schema.gen.ts`
 * `BomListResult.boms`) is already field-for-field identical to this
 * vertical's `LibraryBomRow` view model (`types.ts`) — id/name/status/
 * partCount/estimatedCost/maxLeadTimeDays/sessionId/updatedAt — so this is
 * the same "signature-compatible drop-in" cast `bffListLibrary`
 * (`usePartsLibrary.ts`) uses, not a reshape. Mirrors `useStaging.ts`'s
 * `bffListBoms` in spirit (same `GET /boms` call), but returns the FULL row
 * shape `BomsTable` renders, not the picker's narrower `BomSummary`.
 */
async function bffListBoms(): Promise<readonly LibraryBomRow[]> {
  const { data, error } = await apiClient.GET('/boms')
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t load your BOMs. Try again.'))
  }
  // `boms` is required on the generated `BomListResult` contract type — no
  // `?? []` runtime guard for a shape the contract already guarantees (this
  // repo's "trust generated types" rule).
  return data.boms
}

/**
 * UID-295 (AC4) — inline BOM status change from the card grid. `PATCH /boms/:bomId`
 * with `{ status }`, the SAME op `useBomDetail`'s `updateStatus` uses. Resolves void
 * on success; the hook patches the row's status in the list state.
 */
async function bffUpdateBomStatus(bomId: string, status: BomLibraryStatus): Promise<void> {
  const { error } = await apiClient.PATCH('/boms/{bomId}', {
    params: { path: { bomId } },
    body: { status },
  })
  if (error) {
    throw new Error(problemTitle(error, 'We couldn’t update this BOM’s status. Try again.'))
  }
}

export const defaultBomsService: BomsService = {
  listBoms: bffListBoms,
  updateStatus: bffUpdateBomStatus,
}

export type BomsState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly boms: readonly LibraryBomRow[] }

export interface UseBomsResult {
  readonly state: BomsState
  readonly refetch: () => void
  /** UID-295 (AC4) — change a BOM's status from the card grid. Calls the service,
   * then patches the matching row's `status` in the list state on success (no full
   * refetch — the change is local and the endpoint is authoritative). Rejects with a
   * readable message on failure so the card can surface it. */
  readonly updateStatus: (bomId: string, status: BomLibraryStatus) => Promise<void>
}

/**
 * Loads the Parts Library's BOMs tab from `GET /boms` (UID-290) — see this
 * file's doc comment for why that's a different call than the parts tab's
 * `usePartsLibrary`. Same `loading`/`error`/`ready` state shape as every
 * other data hook in this vertical (`usePartsLibrary`/`useBomDetail`).
 */
export function useBoms(service: BomsService = defaultBomsService): UseBomsResult {
  const [state, setState] = useState<BomsState>({ status: 'loading' })
  const [generation, setGeneration] = useState(0)
  const serviceRef = useRef(service)
  serviceRef.current = service

  // `generation` is a manual re-fetch trigger (bumped by `refetch`) that
  // isn't read in the effect body — it exists purely to force a re-run.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading' })

    serviceRef.current
      .listBoms()
      .then((boms) => {
        if (!cancelled) setState({ status: 'ready', boms })
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message:
              error instanceof Error ? error.message : 'We couldn’t load your BOMs. Try again.',
          })
        }
      })

    return () => {
      cancelled = true
    }
  }, [generation])

  const refetch = useCallback(() => setGeneration((g) => g + 1), [])

  const updateStatus = useCallback(
    async (bomId: string, status: BomLibraryStatus): Promise<void> => {
      const fn = serviceRef.current.updateStatus
      if (!fn) {
        throw new Error('Changing a BOM’s status isn’t available.')
      }
      await fn(bomId, status)
      // Patch the row in place on success (the endpoint is authoritative for the
      // transition; no full refetch needed for a single-field change).
      setState((prev) =>
        prev.status === 'ready'
          ? {
              status: 'ready',
              boms: prev.boms.map((b) => (b.id === bomId ? { ...b, status } : b)),
            }
          : prev,
      )
    },
    [],
  )

  return { state, refetch, updateStatus }
}
